/* eslint-disable rover/no-platform-specific-globals-or-imports, no-underscore-dangle */
import { ComponentType, startTransition } from 'react';
import { hydrateRoot } from 'react-dom/client';
import Loadable from 'react-loadable';

import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';
import initI18n from '@rover/shared/js/utils/initI18n';

import { matchServerSidePath } from './utils/matchServerSidePath';
import removeGoogleCacheUrl from './utils/removeGoogleCacheUrl';
import ClientEntry from './ClientEntry';

const { NODE_ENV } = process.env;
const IS_PROD = NODE_ENV === 'production';
const SUSPENSE_UPDATE_ERROR = 'This Suspense boundary received an update';

const locale = window.__LOCALE__;
const messages = window.__LINGUI_MESSAGES__;

const hydrateApp = (RootComponent: ComponentType): void => {
  const appElement = document.getElementById('app');
  if (!appElement) throw new Error('App element not found.');

  // startTransition enables time slicing in React (aka Concurrent Mode)
  startTransition(() => {
    hydrateRoot(appElement, <RootComponent />, {
      onRecoverableError: (error, errorInfo) => {
        if ((error as Error).message.includes(SUSPENSE_UPDATE_ERROR)) {
          // Don't report this error because it's overwhelming Sentry with
          // volume, it's not critical, and it's not easily actioned.
          return;
        }

        captureError(error as Error, { errorInfo, isRecoverableError: true });
      },
    });
  });
};

function onPreloadReady(): void {
  removeGoogleCacheUrl();
  matchServerSidePath();
  initI18n(locale, messages); // Must be initialized before hydration
  hydrateApp(ClientEntry);
}

Loadable.preloadReady()
  .then(onPreloadReady)
  .catch((e) => {
    captureError(e);
  });

if (IS_PROD) require('@rover/utilities/jobsBlurb');
